import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Receipt, ChevronRight, Plus, RefreshCw, Lock, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function OpsCortes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cortes, setCortes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const isAdmin = ["corporate_admin", "treasury"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (branchFilter) params.set("branch_id", branchFilter);
      const res = await api.get(`/ops/cortes?${params}`);
      setCortes(res.data);
    } catch {
      setCortes([]);
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/branches").then(r => setBranches(r.data.filter(b => !b.is_corporate))).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const fmtCurrency = v => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);

  // Fix WH18 (2026-08-31): POST /ops/cortes/generar-desde-wansoft existe desde
  // WH14 y NO tenia boton — habia que llamarlo por consola. Por eso, medido el
  // 31/08, 104 de 120 dias-sucursal de los ultimos 30 dias seguian sin corte:
  // el hook automatico solo cubre los dias nuevos, y el historico requeria que
  // alguien abriera la consola del navegador. Nunca pisa un corte existente.
  const hoyStr = new Date().toISOString().slice(0, 10);
  const hace60 = new Date(Date.now() - 60 * 864e5).toISOString().slice(0, 10);
  const [showGenerar, setShowGenerar] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [genForm, setGenForm] = useState({ from_date: hace60, to_date: hoyStr, branch_id: "", notificar: true });
  const [genResultado, setGenResultado] = useState(null);

  const handleGenerar = async () => {
    const dias = Math.round((new Date(genForm.to_date) - new Date(genForm.from_date)) / 864e5);
    if (Number.isNaN(dias)) { toast.error("Completa las dos fechas"); return; }
    if (dias < 0) { toast.error("La fecha final no puede ser anterior a la inicial"); return; }
    if (dias > 62) { toast.error("Maximo 62 dias por corrida"); return; }
    setGenerando(true);
    setGenResultado(null);
    try {
      const res = await api.post("/ops/cortes/generar-desde-wansoft", {
        from_date: genForm.from_date,
        to_date: genForm.to_date,
        branch_id: genForm.branch_id || null,
        notificar: genForm.notificar,
      });
      setGenResultado(res.data);
      toast.success(`${res.data.generados} cortes generados desde el POS`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron generar los cortes");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Cortes de Caja</h1>
          <p className="text-sm text-slate-500">Registros diarios por sucursal</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <select
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="cortes-branch-filter"
            >
              <option value="">Todas las sucursales</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="cortes-refresh-btn">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
          {/* Fix WH18 (2026-08-31): generar los cortes que faltan desde el POS. */}
          {user?.role === "corporate_admin" && (
            <button
              onClick={() => { setGenResultado(null); setShowGenerar(true); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="generar-cortes-pos-btn"
            >
              <Sparkles className="w-4 h-4" /> Generar desde POS
            </button>
          )}
          {["corporate_admin", "branch_manager"].includes(user?.role) && (
            <button
              onClick={() => navigate("/ops/cortes/nuevo")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors"
              data-testid="nuevo-corte-btn"
            >
              <Plus className="w-4 h-4" /> Nuevo corte
            </button>
          )}
        </div>
      </div>

      {showGenerar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !generando && setShowGenerar(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()} data-testid="generar-cortes-modal">
            <div>
              <h3 className="font-bold text-slate-900">Generar cortes desde el POS</h3>
              <p className="text-xs text-slate-500 mt-1">
                Arma el corte de cada dia con la venta por forma de pago que reporta Wansoft.
                El gerente despues lo firma y anota diferencias; los montos quedan bloqueados.
                <span className="font-semibold text-slate-700"> Nunca pisa un corte que ya existe.</span>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Desde</label>
                <input type="date" value={genForm.from_date} max={hoyStr} data-testid="generar-desde"
                  onChange={e => setGenForm(f => ({ ...f, from_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Hasta</label>
                <input type="date" value={genForm.to_date} max={hoyStr} data-testid="generar-hasta"
                  onChange={e => setGenForm(f => ({ ...f, to_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Sucursal</label>
              <select value={genForm.branch_id} onChange={e => setGenForm(f => ({ ...f, branch_id: e.target.value }))}
                data-testid="generar-sucursal" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Todas las sucursales</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {/* Fix WH21 (2026-08-31): al rellenar el historico, avisarle al gerente
                de cada dia viejo le llena el telefono de notificaciones inutiles. */}
            <label className="flex items-start gap-2 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3">
              <input type="checkbox" checked={!genForm.notificar} data-testid="generar-sin-avisar"
                onChange={e => setGenForm(f => ({ ...f, notificar: !e.target.checked }))}
                className="mt-0.5" />
              <span className="text-sm text-slate-700">
                <span className="font-semibold">No avisar a los gerentes</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Marcalo cuando estes rellenando dias pasados: los cortes se crean igual,
                  pero sin mandarles una notificacion por cada uno. Para el dia a dia, dejalo sin marcar.
                </span>
              </span>
            </label>
            <p className="text-xs text-slate-400">Maximo 62 dias por corrida.</p>

            {genResultado && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm space-y-1" data-testid="generar-resultado">
                <div><span className="font-bold text-emerald-700">{genResultado.generados}</span> cortes generados</div>
                <div className="text-slate-500"><span className="font-semibold">{genResultado.salteados}</span> dias salteados (ya tenian corte o no hay venta del POS)</div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowGenerar(false)} disabled={generando}
                className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                {genResultado ? "Cerrar" : "Cancelar"}
              </button>
              <button onClick={handleGenerar} disabled={generando} data-testid="generar-confirmar"
                className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-300 disabled:opacity-50">
                {generando ? "Generando..." : "Generar"}
              </button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-20 animate-pulse" />)}
        </div>
      ) : cortes.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto" />
          <p className="text-slate-500">No hay cortes registrados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cortes.map(c => (
            <button
              key={c.id}
              onClick={() => navigate(`/ops/cortes/${c.id}`)}
              className="w-full flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all text-left"
              data-testid={`corte-row-${c.id}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.immutable ? "bg-green-100" : "bg-yellow-50"}`}>
                {c.immutable ? <Lock className="w-5 h-5 text-green-600" /> : <FileText className="w-5 h-5 text-yellow-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{c.branch_name}</div>
                <div className="text-xs text-slate-500">{c.date}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-bold text-slate-900">{fmtCurrency(c.venta_total)}</div>
                {c.source === "wansoft" && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 mr-1">POS</span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.immutable ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-700"}`}>
                  {c.immutable ? "Firmado" : "Borrador"}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
